import { Date_toDateString, Date_toDateTimeString } from "../../../common/dateUtils";
import { Location, WorkOrigin } from "../../../common/enums";
import { FormState_PopulateErrorMessages } from "../../../common/formStateUtils";
import { String_removePrefix } from "../../../common/stringUtils";
import { ParticipantListItem, ParticipantListItem_default } from "../../../components/Forms/Permits/ParticipantsList";
import UnderWaterWorkPermitRequest from "../../requests/UnderWaterWorkPermitRequest";
import Errors from "../../responses/Errors";
import FormElement, { formElement } from "../FormElement";
import PersonFormElement, {
    PersonFormElement_default,
    PersonFormElement_populateErrorMessages,
    PersonFormElement_toPerson,
    PersonFormElements_populateErrorMessages
} from "../PersonFormElement";
import PositionPickerRow, {
    PositionPickerRow_default,
    PositionPickerRow_toPosition,
    PositionPickerRows_populateErrorMessages
} from "./PositionPickerRow";

type UnderWaterWorkPermitFormState = {
    remindMe: FormElement<boolean>;
    remindMeDate: FormElement<Date>;
    startDate: FormElement<Date>;
    endDate: FormElement<Date>;
    adminComment: FormElement<string>;
    description: FormElement<string>;
    positions: PositionPickerRow[];
    expirationReminderEmail: FormElement<string>;
    expirationReminderPhoneNumber: FormElement<string>;
    entrepreneur: FormElement<string>;
    persons: ParticipantListItem[];
    responsibleWorkLeader: PersonFormElement;
    projectLeader: PersonFormElement;
    companyOrgNr: FormElement<string>;
    vhfIsAvailable: FormElement<boolean>;
    workOrigin: FormElement<WorkOrigin>;
    workLocation: FormElement<Location>;
    energyHarbourSequenceIdentifier: FormElement<string>;
};

export default UnderWaterWorkPermitFormState;

export function UnderWaterWorkPermitFormState_default(): UnderWaterWorkPermitFormState {
    const defaultFormState: UnderWaterWorkPermitFormState = {
        adminComment: formElement(""),
        description: formElement(""),
        endDate: formElement(new Date()),
        entrepreneur: formElement(""),
        expirationReminderEmail: formElement(""),
        expirationReminderPhoneNumber: formElement(""),
        positions: [PositionPickerRow_default()],
        remindMe: formElement(false),
        companyOrgNr: formElement(""),
        remindMeDate: formElement(new Date()),
        persons: [ParticipantListItem_default()],
        projectLeader: PersonFormElement_default(),
        responsibleWorkLeader: PersonFormElement_default(),
        startDate: formElement(new Date()),
        vhfIsAvailable: formElement(false),
        workLocation: formElement(Location.General),
        workOrigin: formElement(WorkOrigin.Quay),
        energyHarbourSequenceIdentifier: formElement("")
    };

    return defaultFormState;
}

export function UnderWaterWorkPermitFormState_toRequest(
    formState: UnderWaterWorkPermitFormState
): UnderWaterWorkPermitRequest {
    const request: UnderWaterWorkPermitRequest = {
        adminComment: formState.adminComment.value,
        description: formState.description.value,
        companyOrgNr: formState.companyOrgNr.value,
        endDate: Date_toDateTimeString(formState.endDate.value),
        entrepreneur: formState.entrepreneur.value,
        expirationReminderEmail: formState.expirationReminderEmail.value,
        expirationReminderPhoneNumber: formState.expirationReminderPhoneNumber.value,
        positions: formState.positions.map(PositionPickerRow_toPosition),
        persons: formState.persons.map(PersonFormElement_toPerson),
        projectLeader: PersonFormElement_toPerson(formState.projectLeader),
        remindMe: formState.remindMe.value,
        remindMeDate: Date_toDateString(formState.remindMeDate.value),
        responsibleWorkLeader: PersonFormElement_toPerson(formState.responsibleWorkLeader),
        startDate: Date_toDateTimeString(formState.startDate.value),
        vhfIsAvailable: formState.vhfIsAvailable.value,
        workLocation: formState.workLocation.value,
        workOrigin: formState.workOrigin.value,
        energyHarbourSequenceIdentifier: formState.energyHarbourSequenceIdentifier.value
    };

    return request;
}

export function UnderWaterWorkPermitFormState_populateErrorMessages(
    formState: UnderWaterWorkPermitFormState,
    errors: Errors
): UnderWaterWorkPermitFormState {
    const errorKeys = Object.keys(errors);

    const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
    const positionErrors = Object.fromEntries(
        positionsErrorKeys.map((k) => [String_removePrefix(k, "Positions"), errors[k]])
    );

    const personErrorKeys = errorKeys.filter((e) => e.startsWith("Persons"));
    const personErrors = Object.fromEntries(personErrorKeys.map((k) => [String_removePrefix(k, "Persons"), errors[k]]));

    const projectLeaderErrorKeys = errorKeys.filter((e) => e.startsWith("ProjectLeader."));
    const projectLeaderErrors = Object.fromEntries(
        projectLeaderErrorKeys.map((k) => [String_removePrefix(k, "ProjectLeader."), errors[k]])
    );

    const responsibleWorkLeaderErrorKeys = errorKeys.filter((e) => e.startsWith("ResponsibleWorkLeader."));
    const responsibleWorkLeaderErrors = Object.fromEntries(
        responsibleWorkLeaderErrorKeys.map((k) => [String_removePrefix(k, "ResponsibleWorkLeader."), errors[k]])
    );

    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);
    updatedFormState.positions = PositionPickerRows_populateErrorMessages(formState.positions, positionErrors);

    updatedFormState.projectLeader = PersonFormElement_populateErrorMessages(
        formState.projectLeader,
        projectLeaderErrors
    );
    updatedFormState.responsibleWorkLeader = PersonFormElement_populateErrorMessages(
        formState.responsibleWorkLeader,
        responsibleWorkLeaderErrors
    );

    updatedFormState.projectLeader = PersonFormElement_populateErrorMessages(
        formState.projectLeader,
        projectLeaderErrors
    );

    updatedFormState.persons = PersonFormElements_populateErrorMessages(formState.persons, personErrors);

    return updatedFormState;
}
